import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { loyaltyAPI } from '../api/loyaltyAPI';
import { buildStaticLoyaltyQr, isNetworkError } from '../libs/qrValue';
import { readLoyaltyUserId } from '../libs/qrStorage';
import { IDynamicLoyaltyQr, LoyaltyQrMode } from '../types/LoyaltyQr';
import { userInfo } from '../../../store/selectors/user';
import { useOnlineStatus } from '../../../lib/useOnlineStatus';
import { mindboxUserInfoQueryKey } from '../../home/services/getMindboxUserInfo';

/** Сколько код живёт, если бэкенд не сказал, — по документации 60 секунд. */
const DEFAULT_LIFETIME_MS = 60_000;
/** Бэкенд ответил ошибкой при живой сети — пробуем снова, но не долбим. */
const SERVER_ERROR_RETRY_MS = 10_000;
/**
 * Запрос не дошёл до сервера — проверяем сеть повторно. В WKWebView (Telegram
 * на iOS) `navigator.onLine` и события online/offline ненадёжны: возврат сети
 * там иначе не заметить, и статичный код висел бы до закрытия шторки.
 */
const NETWORK_RETRY_MS = 5_000;

export const loyaltyQrQueryKey = ['loyalty-qr'] as const;

const toDynamicQr = (data: {
  qr_value: string;
  expires_in?: number;
  expires_at: string;
}): IDynamicLoyaltyQr => {
  const receivedAt = Date.now();
  // expires_in надёжнее: он не зависит от часов устройства. expires_at —
  // запасной путь для ответа без него.
  const lifetimeMs = data.expires_in
    ? data.expires_in * 1000
    : Math.max(0, new Date(data.expires_at).getTime() - receivedAt) ||
      DEFAULT_LIFETIME_MS;

  return {
    value: data.qr_value,
    expiresAtMs: receivedAt + lifetimeMs,
    lifetimeMs,
  };
};

export type LoyaltyQrState = {
  mode: LoyaltyQrMode;
  /** Что рисовать. null — рисовать нечего, см. mode. */
  value: string | null;
  /** Только у динамического кода: для прогресс-бара и обратного отсчёта. */
  expiresAtMs?: number;
  lifetimeMs?: number;
};

/**
 * QR лояльности: динамический с бэкенда, статичный офлайн.
 *
 * Правила — из спецификации, в порядке приоритета:
 *   1. Сети нет (событие браузера или запрос не дошёл) — офлайн-экран со
 *      статичным кодом, сразу: динамический код и его таймер с этого момента
 *      не используются. Статичный — тот же формат с версией 2 и id
 *      пользователя вместо токена. Нет id — нет кода.
 *   1а. Сеть есть и есть живой динамический код — показываем его.
 *   2. Сеть есть — просим бэкенд и обновляемся заранее, до истечения.
 *      Ошибка сервера при живой сети — это не повод для статичного кода:
 *      показываем ошибку и пробуем снова.
 *   4. Сеть вернулась — запрос уходит сам (по событию браузера или по
 *      периодической проверке, где событий нет — iOS), и статичный код
 *      сменяется динамическим без участия пользователя.
 *
 * `open` — экран открыт: код живёт секунды, и просить его впрок незачем.
 */
export const useLoyaltyQr = (open: boolean): LoyaltyQrState => {
  const user = useSelector(userInfo);
  const online = useOnlineStatus();
  const isAuth = user.isAuth && Boolean(user._id);

  const enabled = open && isAuth && online;
  const queryClient = useQueryClient();

  const { data, error, refetch, isFetching } = useQuery({
    queryKey: loyaltyQrQueryKey,
    queryFn: async () => {
      const response = (await loyaltyAPI.getQr()).data;

      // Вместе с кодом приходит свежий баланс из Mindbox — кладём его туда,
      // откуда его читают карта лояльности и шторка QR.
      if (response.customer?.balances?.length) {
        queryClient.setQueryData(mindboxUserInfoQueryKey, { data: response });
      }

      return toDynamicQr(response);
    },
    enabled,
    // Сроком жизни управляем сами (интервал, таймер истечения, эффект
    // открытия ниже). Для react-query код «свежий» всегда — иначе при каждом
    // повторном включении запроса (вернулись из истории) он тянул бы новый
    // токен, и таймер на экране обнулялся бы без причины.
    staleTime: Infinity,
    // Живой код обновляет таймер истечения ниже — ровно по сроку жизни, не
    // раньше; интервал нужен только для повторов после ошибки. Пока браузер
    // сам сообщает об отсутствии сети, запрос выключен вовсе (см. enabled);
    // сетевая ошибка при «живой» по мнению браузера сети — повод опрашивать:
    // на iOS это единственный способ узнать, что сеть вернулась. Статичный код на экране при этом остаётся —
    // до успешного ответа error не сбрасывается.
    refetchInterval: query => {
      if (query.state.error) {
        return isNetworkError(query.state.error) ? NETWORK_RETRY_MS : SERVER_ERROR_RETRY_MS;
      }

      return false;
    },
    // Сетевые ошибки не ретраим: их лечит не повтор, а возврат сети — и на
    // это время экран должен показывать статичный код, а не крутилку.
    retry: (count, err) => !isNetworkError(err) && count < 1,
    refetchOnWindowFocus: false,
    // В webview Telegram документ бывает «не в фокусе» с точки зрения
    // react-query, и интервал тогда замирает — код истекал без обновления.
    refetchIntervalInBackground: true,
  });

  // Истечение кода должно перерисовать экран само: ждём ровно до дедлайна —
  // и тут же просим новый, не дожидаясь интервала.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!data) return;

    const delay = Math.max(data.expiresAtMs - Date.now(), 0) + 50;
    const timer = window.setTimeout(() => {
      setNow(Date.now());
      if (enabled) refetch({ cancelRefetch: false });
    }, delay);

    return () => window.clearTimeout(timer);
  }, [data, enabled, refetch]);

  // Экран открыли заново (например, вернулись из истории). Живой код
  // показываем как есть — с тем же остатком времени; нет живого — просим.
  useEffect(() => {
    if (!enabled || isFetching) return;

    const alive = data && data.expiresAtMs > Date.now();
    if (!alive) refetch();
    // Только по открытию: остальные перезапросы делают интервал и таймер.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const dynamicAlive = data && data.expiresAtMs > Math.max(now, Date.now());

  // На стенде печатаем состояние, из которого выбрался режим: без этого
  // «висит Формируем…» не отличить от «ждём ответ» и «код истёк».
  if (import.meta.env.DEV && open) {
    console.info('[loyalty-qr]', {
      isAuth,
      online,
      enabled,
      isFetching,
      hasData: Boolean(data),
      expiresInS: data ? Math.round((data.expiresAtMs - Date.now()) / 1000) : null,
      error: error ? String(error) : null,
    });
  }

  if (!isAuth) {
    return { mode: 'none', value: null };
  }

  // Сеть отсутствует достоверно: об этом сказал браузер или это показал сам
  // запрос — до сервера не дошёл. Всё остальное — ошибки сервера при живой
  // сети, и там статичный код не нужен.
  //
  // Проверяется раньше живого динамического кода: по спеке WebApp при потере
  // сети экран переключается на офлайн сразу — динамический код и его таймер
  // с этого момента не используются, даже если срок ещё не вышел.
  const offline = !online || (Boolean(error) && isNetworkError(error));

  if (offline) {
    // Id из стора, а без профиля (открыли уже без сети) — сохранённый.
    const userId = user._id || readLoyaltyUserId();

    return userId
      ? { mode: 'static', value: buildStaticLoyaltyQr(userId) }
      : { mode: 'offline-no-id', value: null };
  }

  if (dynamicAlive) {
    return {
      mode: 'dynamic',
      value: data.value,
      expiresAtMs: data.expiresAtMs,
      lifetimeMs: data.lifetimeMs,
    };
  }

  // Повторная попытка после ошибки идёт в фоне: мигать «загрузкой» раз в
  // десять секунд незачем, текст об ошибке точнее описывает происходящее.
  if (error) {
    return { mode: 'error', value: null };
  }

  return { mode: 'loading', value: null };
};

/** Ключ i18n подписи для режимов, в которых код нарисовать нечем. */
export const loyaltyQrPlaceholderKey = (mode: LoyaltyQrMode): string | undefined => {
  switch (mode) {
    case 'loading':
      return 'qr-loading';
    case 'error':
      return 'qr-unavailable';
    case 'offline-no-id':
      return 'qr-offline-no-id';
    default:
      return undefined;
  }
};
